import PropTypes from "prop-types";
import React, { useEffect } from "react";
import { FormControl, FormGroup, FormSelect } from "react-bootstrap";
import { useIntl } from "react-intl";
import { OTHER_ANSWER } from "../../../../../common/constant";
import ObjectUtils, { coalesce } from "../../../../../utils/ObjectUtils";
import { findAnswer, isSimpleType, makeAnswer, makeSimpleAnswer } from "../../../../../utils/QuestionUtils";
import Radio from "../../../../common/Radio";
import InfoTooltip from "./InfoTooltip";

const { findByObjectProp, getDeepProperty, opt } = ObjectUtils;

const RadioRenderer = ({
  disabled,
  question,
  collection,
  setQuestionAnswer,
  removeQuestionAnswer,
  row,
  column,
  showOnlySelected,
  onFilter,
  isActivityMonitoring,
  isIncomplete,
}) => {
  const intl = useIntl();
  const answer = findAnswer(collection, {
    row,
    column
  });
  const simpleType = isSimpleType(question.type);
  const type = coalesce(getDeepProperty(column, "type"), question.type, "text").toLowerCase();
  const choices = coalesce(getDeepProperty(column, "choices"), getDeepProperty(question, "choices"), []);
  const hasOther = coalesce(getDeepProperty(column, "hasOther"), getDeepProperty(question, "hasOther"), false);
  const otherAnswer = findAnswer(collection, {
    row,
    column,
    name: OTHER_ANSWER
  });

  useEffect(() => {
    if (answer && onFilter) {
      onFilter(answer.text);
    }
  }, [answer, onFilter]);

  const handleRadioChange = (choice, checked) => {
    if (checked) {
      removeQuestionAnswer(
        question,
        simpleType ? makeSimpleAnswer(choice.value, question) : makeAnswer(choice.value, row, column)
      );
    } else {
      setQuestionAnswer(
        question,
        simpleType ? makeSimpleAnswer(choice.value, question) : makeAnswer(choice.value, row, column)
      );
      if (onFilter) {
        onFilter(choice.text);
      }
    }
  };

  const handleRadioOtherChange = () => {
    if (opt(otherAnswer).name === OTHER_ANSWER) {
      removeQuestionAnswer(
        question,
        simpleType
          ? makeSimpleAnswer(undefined, question, OTHER_ANSWER)
          : makeAnswer(undefined, row, column, OTHER_ANSWER)
      );
    } else {
      setQuestionAnswer(
        question,
        simpleType
          ? makeSimpleAnswer(undefined, question, OTHER_ANSWER)
          : makeAnswer(undefined, row, column, OTHER_ANSWER)
      );
    }
  };

  const handleDropDownChange = (event) => {
    const value = event.target.value;
    if (value === "0") {
      removeQuestionAnswer(
        question,
        simpleType ? makeSimpleAnswer(undefined, question) : makeAnswer(undefined, row, column)
      );
      if (onFilter) {
        onFilter("");
      }
    } else if (value === OTHER_ANSWER) {
      setQuestionAnswer(
        question,
        simpleType
          ? makeSimpleAnswer(undefined, question, OTHER_ANSWER)
          : makeAnswer(undefined, row, column, OTHER_ANSWER)
      );
    } else {
      const ans = simpleType ? makeSimpleAnswer(value, question) : makeAnswer(value, row, column);
      setQuestionAnswer(question, ans);
      if (onFilter) {
        onFilter(ans.text);
      }
    }
  };

  const handleOtherChange = (event) => {
    const answer = simpleType
      ? makeSimpleAnswer(event.target.value, question, OTHER_ANSWER)
      : makeAnswer(event.target.value, row, column, OTHER_ANSWER);
    if (onFilter) {
      onFilter(answer);
    }
    setQuestionAnswer(question, answer);
  };

  const renderRadio = () => {
    return (
      <>
        {choices.map((choice, i) => {
          const checked = choice.value === opt(answer).value;

          return (
            <React.Fragment key={`${choice.value}-${i}`}>
              {(checked || !showOnlySelected) && (
                <Radio
                  name={question.name}
                  checked={checked}
                  disabled={disabled}
                  onClick={() => handleRadioChange(choice, checked)}
                >
                  {choice.text}
                  {choice.infoText && (
                    <>
                      &nbsp; &nbsp;
                      <InfoTooltip infoText={choice.infoText} />
                    </>
                  )}
                </Radio>
              )}
            </React.Fragment>
          );
        })}
        {hasOther && (
          <Radio name={question.name} disabled={disabled} onClick={handleRadioOtherChange} checked={!!otherAnswer}>
            {intl.formatMessage({ id: "user.surveyView.radio.otherChoice.input" })}
          </Radio>
        )}
      </>
    );
  };

  const renderDropdown = () => {
    const choice = answer ? findByObjectProp(choices, "value", answer.value) : null;
    const choiceLabel = coalesce(
      getDeepProperty(choice, "text"),
      intl.formatMessage({ id: "user.surveyView.dropdown.choose" })
    );

    let choiceValue = "0";
    if (answer && answer.name === OTHER_ANSWER) {
      choiceValue = OTHER_ANSWER;
    } else if (answer && answer.value) {
      choiceValue = answer.value;
    }

    return (
      <FormSelect
        disabled={disabled}
        value={choiceValue}
        title={otherAnswer ? intl.formatMessage({ id: "user.surveyView.radio.otherChoice" }) : choiceLabel}
        onChange={handleDropDownChange}
      >
        <option value="0" key={`${question.name}clearOption`} disabled={disabled}>
          {intl.formatMessage({ id: "user.surveyView.dropdown.choose" })}
        </option>

        {choices.map((choice, i) => (
          <option key={`${choice.value}-${i}`} value={choice.value} disabled={disabled}>
            {choice.germanName ? choice.germanName : choice.text}
          </option>
        ))}
        {hasOther && (
          <option key={`${question.name}otherOption`} value={OTHER_ANSWER} disabled={disabled}>
            {intl.formatMessage({ id: "user.surveyView.radio.otherChoice.input" })}
          </option>
        )}
      </FormSelect>
    );
  };

  const isRadioType = type === "radio";
  return (
    <FormGroup className={`mb-3 ${!isRadioType && isIncomplete ? "border border-2 border-danger" : ""}`} controlId={question.name} >
      {isRadioType ? renderRadio() : renderDropdown()}
      {opt(answer).name === OTHER_ANSWER && (
        <FormControl
          disabled={disabled}
          placeholder={intl.formatMessage({ id: "user.surveyView.radio.otherChoice" })}
          onChange={handleOtherChange}
          defaultValue={coalesce(opt(answer).value, "")}
        />
      )}

      {opt(answer).value && isActivityMonitoring && (
        <h5 className={"selection-preview"}>
          <strong>Auswahl: </strong> {opt(answer).text}
        </h5>
      )}
    </FormGroup>
  );
};

RadioRenderer.propTypes = {
  disabled: PropTypes.bool,
  question: PropTypes.object.isRequired,
  collection: PropTypes.array,
  setQuestionAnswer: PropTypes.func,
  removeQuestionAnswer: PropTypes.func,
  row: PropTypes.object,
  column: PropTypes.object,
  intl: PropTypes.object,
  showOnlySelected: PropTypes.bool,
  onFilter: PropTypes.func,
  isActivityMonitoring: PropTypes.bool,
  isIncomplete: PropTypes.bool,
};

export default React.memo(RadioRenderer);
